import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface PlanFeature { name: string; included: boolean; }
interface Plan {
  name: string;
  price: string;
  period?: string;
  features: PlanFeature[];
  cta_text?: string;
  cta_url?: string;
  highlighted?: boolean;
}

const ComparisonTable: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const plans = (c.plans || []) as Plan[];

  return (
    <section className="sb-render sb-render-comparison-table" style={{ padding: '80px 24px', background: '#f8fafc' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.025em', marginBottom: 12 }}
          >
            {sfHeadline(c, 'Compare Plans')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)' }}
          >
            {sfSub(c, 'Choose the plan that works for you.')}
          </InlineEditText>
        </div>

        {/* Plans grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(plans.length, 1)}, 1fr)`,
          gap: 24,
          alignItems: 'start',
        }}>
          {plans.map((plan, pi) => (
            <div
              key={pi}
              style={{
                background: '#fff',
                borderRadius: 'var(--brand-card-radius, 12px)',
                border: plan.highlighted ? '2px solid var(--brand-primary, #2563eb)' : '1px solid #e2e8f0',
                overflow: 'hidden',
                position: 'relative',
                boxShadow: plan.highlighted ? '0 8px 32px rgba(37,99,235,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {plan.highlighted && (
                <div style={{
                  background: 'var(--brand-primary, #2563eb)',
                  color: '#fff',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  padding: '6px 0',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>
                  Most Popular
                </div>
              )}

              {/* Plan header */}
              <div style={{ padding: '28px 24px 20px' }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 8, color: '#0f172a' }}>
                  {plan.name || `Plan ${pi + 1}`}
                </h3>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a' }}>
                    {plan.price || '—'}
                  </span>
                  {plan.period && (
                    <span style={{ color: 'var(--brand-text-light, #64748b)', fontSize: '0.9rem' }}>
                      /{plan.period}
                    </span>
                  )}
                </div>
              </div>

              {/* Features list */}
              <ul style={{ listStyle: 'none', padding: '0 24px', margin: 0, borderTop: '1px solid #f1f5f9' }}>
                {(plan.features || []).map((feat, fi) => (
                  <li key={fi} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '11px 0',
                    borderBottom: '1px solid #f1f5f9',
                    fontSize: '0.9rem',
                    color: feat.included ? '#0f172a' : '#94a3b8',
                  }}>
                    <span style={{
                      fontSize: '1rem',
                      color: feat.included ? '#16a34a' : '#cbd5e1',
                      flexShrink: 0,
                    }}>
                      {feat.included ? '✓' : '✕'}
                    </span>
                    {feat.name}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div style={{ padding: '20px 24px' }}>
                <a
                  href={plan.cta_url || '#'}
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '12px',
                    borderRadius: 'var(--brand-btn-radius, 6px)',
                    background: plan.highlighted ? 'var(--brand-primary, #2563eb)' : 'transparent',
                    border: plan.highlighted ? 'none' : '1px solid #e2e8f0',
                    color: plan.highlighted ? '#fff' : '#0f172a',
                    fontWeight: 700,
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s',
                  }}
                >
                  {plan.cta_text || 'Get Started'}
                </a>
              </div>
            </div>
          ))}

          {plans.length === 0 && (
            <p style={{ textAlign: 'center', color: '#94a3b8', gridColumn: '1 / -1', padding: '32px 0' }}>
              No plans configured yet.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;
